import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import comms from '../comms';
import TimeStepper from '../ui/TimeStepper';
import LocationPicker from './LocationPicker';
import SplitRouteDialog from './SplitRouteDialog';
import { fmtServiceDate, toWallClockHM } from './schedulingUtils';

// Server datetime → "HH:MM" for the editor, as WALL-CLOCK digits with no
// timezone conversion — symmetric with composeDateTime() below, which writes
// bare digits. Previously `new Date(v).getHours()`, which re-projected the
// server's (falsely) "Z"-marked naive digits into the browser's zone and shifted
// every field by the local UTC offset on each reload. See wallClockParts().
const toLocalHM = toWallClockHM;

// serviceDate + "HH:MM" (+ dayOffset days) → fleet-local "YYYY-MM-DDTHH:MM:SS"
// (no Z — server treats as fleet-local). dayOffset rolls a time onto the next
// day so overnight routes/breaks (e.g. shift 12:05 → 04:30, break 18:30 → 00:30)
// compose to the correct calendar date instead of landing before the start.
function composeDateTime(serviceDate, hm, dayOffset = 0) {
  if (!hm) return null;
  const [y, mo, d] = String(serviceDate).slice(0, 10).split('-').map(Number);
  const dt = new Date(y, mo - 1, d + dayOffset);
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${hm}:00`;
}

export function hmMins(hm) {
  if (!hm) return null;
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + (m || 0);
}

// Day offset for a shift time relative to the shift start. Anything earlier
// in the clock than the start rolls to the next day (overnight shift).
export function dayOffsetFrom(startHM, hm) {
  const s = hmMins(startHM);
  const t = hmMins(hm);
  if (s == null || t == null) return 0;
  return t < s ? 1 : 0;
}

// Absolute minute on the shift timeline (0 = start's midnight), midnight-aware.
export function absMins(startHM, hm) {
  const t = hmMins(hm);
  if (t == null) return null;
  return dayOffsetFrom(startHM, hm) * 1440 + t;
}

// serviceDate + "HH:MM" (+ dayOffset days) → "YYYY-MM-DDTHH:MM:00.000Z".
// SplitRoute's strictDateTime() requires a timezone marker, unlike ModifyRoute's bare
// string — but it treats a "Z"-suffixed value as those EXACT digits with zero adjustment
// (see autodisp-master lib/flagTripPolicy... no, lib/weeklyPlanning/driverScheduling.js
// strictDateTime: offsetMinutes stays 0 for 'Z'). Route.StartDateTime/EndDateTime (the
// @SourceStart/@SourceEnd this gets compared against) are themselves naive fleet-local
// digits with NO real UTC conversion anywhere in this app (composeDateTime above is the
// same convention). So this must NOT do a real local->UTC conversion (e.g. via
// `new Date(...).toISOString()`, which shifts the digits by whatever the browser's actual
// OS timezone is) — it must stamp "Z" directly onto the unconverted digits, exactly like
// composeDateTime, or every segment fails the @SourceStart/@SourceEnd window check
// (51005 INVALID_SEGMENT_TIMING) on any machine not set to UTC+0.
export function composeDateTimeUtcIso(serviceDate, hm, dayOffset = 0) {
  if (!hm) return null;
  const [y, mo, d] = String(serviceDate).slice(0, 10).split('-').map(Number);
  const dt = new Date(y, mo - 1, d + dayOffset);
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${hm}:00.000Z`;
}

function initLocsFromSteps(steps, route) {
  const startStep = steps.find((s) => s.type === 'start' || s.stepType === 'start');
  const endStep   = steps.find((s) => s.type === 'end'   || s.stepType === 'end');
  return {
    startLoc: {
      id:   startStep?.locationId || startStep?.LocationId || route.startVehicleLocationId || null,
      name: startStep?.locationName || startStep?.LocationName || route.startLocationName || '',
    },
    endLoc: {
      id:   endStep?.locationId || endStep?.LocationId || route.endVehicleLocationId || null,
      name: endStep?.locationName || endStep?.LocationName || route.endLocationName || '',
    },
  };
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#94A3B8',
        textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function LocButton({ loc, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', textAlign: 'left',
        background: '#F8FAFC', border: '1.5px solid #E2E8F0',
        borderRadius: 12, padding: '12px 14px', cursor: 'pointer', marginTop: 8,
      }}
    >
      <div>
        <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
          Location
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: loc.name ? '#0F172A' : '#CBD5E1' }}>
          {loc.name || 'Choose location…'}
        </div>
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

function RouteEditorSheet({ open, onClose, route, driverId, online, onToast, onSaved, onSplit }) {
  const [start,      setStart]      = useState('');
  const [end,        setEnd]        = useState('');
  const [breakStart, setBreakStart] = useState('');
  const [breakEnd,   setBreakEnd]   = useState('');
  const [startLoc,   setStartLoc]   = useState({ id: null, name: '' });
  const [endLoc,     setEndLoc]     = useState({ id: null, name: '' });
  const [pickerFor,  setPickerFor]  = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [steps,      setSteps]      = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [splitOpen,  setSplitOpen]  = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  useEffect(() => {
    if (!open || !route) return;
    setStart(toLocalHM(route.StartDateTime));
    setEnd(toLocalHM(route.EndDateTime));
    setBreakStart(toLocalHM(route.BreakStartDateTime));
    setBreakEnd(toLocalHM(route.BreakEndDateTime));
    setPickerFor(null);
    setError('');
    setSteps([]);

    const fallbackLocs = {
      startLoc: { id: route.startVehicleLocationId || null, name: route.startLocationName || '' },
      endLoc:   { id: route.endVehicleLocationId   || null, name: route.endLocationName   || '' },
    };

    // If steps were pre-loaded by DaySchedule, use them — no extra fetch needed.
    if (Array.isArray(route.steps) && route.steps.length > 0) {
      setSteps(route.steps);
      const locs = initLocsFromSteps(route.steps, route);
      setStartLoc(locs.startLoc.name ? locs.startLoc : fallbackLocs.startLoc);
      setEndLoc(locs.endLoc.name     ? locs.endLoc   : fallbackLocs.endLoc);
      return;
    }

    setStartLoc(fallbackLocs.startLoc);
    setEndLoc(fallbackLocs.endLoc);

    const routeId = route.RouteId || route.routeId;
    if (!routeId) return;

    setLoadingDetail(true);
    comms.getRouteDetail({ driverId, routeId })
      .then((res) => {
        const itinerary =
          res?.schedule?.itinerary || res?.itinerary || res?.steps || [];
        if (itinerary.length) {
          setSteps(itinerary);
          const locs = initLocsFromSteps(itinerary, route);
          if (locs.startLoc.name) setStartLoc(locs.startLoc);
          if (locs.endLoc.name)   setEndLoc(locs.endLoc);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }, [open, route, driverId]);

  if (!route) return null;

  const serviceDate = route.ServiceDate || String(route.StartDateTime || '').slice(0, 10);

  const tripSteps = steps.filter(
    (s) => s.type === 'pickup' || s.type === 'delivery' || s.type === 'dropoff' ||
           s.stepType === 'pickup' || s.stepType === 'delivery' || s.stepType === 'dropoff'
  );
  const hasTrips     = tripSteps.length > 0;
  const stepTime     = (s) => s.time || s.arrivalTime || s.arrivalDateTime || s.targetDateTime || '';

  // Splitting requires a trip-free, strictly-future service date (server enforces
  // this authoritatively too — see NOT_FUTURE_SERVICE_DATE / ROUTE_HAS_TRIPS).
  const todayStr = (() => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();
  const canSplit = !hasTrips && serviceDate > todayStr;

  const breakStep = steps.find((s) => s.type === 'break' || s.stepType === 'break');
  const hasBreak  = !!breakStep || !!(route.BreakStartDateTime || route.BreakEndDateTime);

  // §4.7 caveats. Break times are editable only before any pickup/dropoff steps
  // exist on the route; once trips are seeded the server rejects break edits.
  const breakLocked = hasTrips;
  // First/last stop as absolute minutes on the shift timeline (midnight-aware,
  // anchored to the current start) so start/end can't intrude on a stop.
  const stepHM       = (s) => toLocalHM(stepTime(s));
  const firstTripAbs = hasTrips ? absMins(start, stepHM(tripSteps[0])) : null;
  const lastTripAbs  = hasTrips ? absMins(start, stepHM(tripSteps[tripSteps.length - 1])) : null;

  const breakMins = breakStart && breakEnd
    ? (() => { const d = hmMins(breakEnd) - hmMins(breakStart); return d > 0 ? d : null; })()
    : null;

  const err = (msg) => { setError(msg); };

  const isRouteActive = !(route.IsInactive === true || route.IsInactive === 1);

  const handleToggleActive = async () => {
    if (!online || togglingActive) return;
    setError('');
    setTogglingActive(true);
    const nextActive = !isRouteActive;
    try {
      const res = await comms.setRouteActive({ driverId, routeId: route.RouteId, active: nextActive });
      if (res?.status === true) {
        onToast(nextActive ? 'Route enabled — dispatch can add trips again.' : 'Route disabled — no new trips will be added.');
        onSaved({ ...route, IsInactive: !nextActive });
      } else {
        err(res?.error || res?.message || "Couldn't update this route — try again.");
      }
    } catch (e) {
      err(e?.message || "Couldn't update this route — try again.");
    } finally {
      setTogglingActive(false);
    }
  };

  const handleSave = async () => {
    setError('');
    if (!online) { err('Connect to the internet to save.'); return; }

    // Both break fields must be set together if either is set.
    if ((breakStart && !breakEnd) || (!breakStart && breakEnd)) {
      err('Set both break start and end, or remove the break.'); return;
    }
    // Time validation is midnight-aware: an overnight shift/break (end earlier
    // on the clock than start) is valid — the end is on the next day.
    if (start && end && absMins(start, end) <= absMins(start, start)) {
      err('End time must be after start time.'); return;
    }
    if (breakStart && breakEnd && absMins(start, breakEnd) <= absMins(start, breakStart)) {
      err('Break end must be after break start.'); return;
    }

    const startChanged = start !== toLocalHM(route.StartDateTime);
    const endChanged   = end   !== toLocalHM(route.EndDateTime);

    // §4.7: a changed start can't fall after the first stop; a changed end can't
    // fall before the last stop. (Server enforces this too — mirror it here.)
    if (hasTrips && startChanged && start && firstTripAbs != null &&
        absMins(start, start) > firstTripAbs) {
      err("Start time can't be after the first stop on this route."); return;
    }
    if (hasTrips && endChanged && end && lastTripAbs != null &&
        absMins(start, end) < lastTripAbs) {
      err("End time can't be before the last stop on this route."); return;
    }

    const body = { driverId, routeId: route.RouteId };
    if (startChanged) body.startDateTime = composeDateTime(serviceDate, start, 0);
    if (endChanged)   body.endDateTime   = composeDateTime(serviceDate, end, dayOffsetFrom(start, end));
    // §4.7: break times only change when the route has no pickup/dropoff steps.
    if (!breakLocked) {
      if (breakStart !== toLocalHM(route.BreakStartDateTime)) body.breakStartDateTime = composeDateTime(serviceDate, breakStart, dayOffsetFrom(start, breakStart));
      if (breakEnd   !== toLocalHM(route.BreakEndDateTime))   body.breakEndDateTime   = composeDateTime(serviceDate, breakEnd, dayOffsetFrom(start, breakEnd));
    }
    if (startLoc.id && startLoc.id !== (route.startVehicleLocationId || null))
      body.startVehicleLocationId = startLoc.id;
    if (endLoc.id && endLoc.id !== (route.endVehicleLocationId || null))
      body.endVehicleLocationId = endLoc.id;

    const changed = Object.keys(body).filter((k) => !['driverId', 'routeId'].includes(k));
    if (!changed.length) { onClose(); return; }

    setSaving(true);
    try {
      const res = await comms.modifyRoute(body);
      if (res?.status === true || res?.Status === true || res?.success === true) {
        onToast('Route saved.');
        onSaved({
          ...route,
          StartDateTime:      body.startDateTime      || route.StartDateTime,
          EndDateTime:        body.endDateTime        || route.EndDateTime,
          BreakStartDateTime: body.breakStartDateTime ?? route.BreakStartDateTime,
          BreakEndDateTime:   body.breakEndDateTime   ?? route.BreakEndDateTime,
          startVehicleLocationId: startLoc.id   || route.startVehicleLocationId,
          startLocationName:      startLoc.name || route.startLocationName,
          endVehicleLocationId:   endLoc.id     || route.endVehicleLocationId,
          endLocationName:        endLoc.name   || route.endLocationName,
        });
        onClose();
      } else {
        err(res?.error || res?.message || "Couldn't save — try again.");
      }
    } catch (e) {
      err(e?.message || "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ style: { borderRadius: 20, margin: 16, maxHeight: 'calc(100% - 32px)' } }}
      >
        <DialogTitle style={{ padding: '20px 20px 4px' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>
            {route.RouteName || 'Edit Route'}
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#94A3B8', marginTop: 2 }}>
            {fmtServiceDate(serviceDate)}
            {loadingDetail && <span style={{ marginLeft: 8 }}>Loading…</span>}
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: '16px 20px' }}>
          {/* Inline error banner */}
          {error && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
              padding: '10px 14px', marginBottom: 16,
              fontSize: 13, color: '#991B1B', fontWeight: 500, lineHeight: 1.4,
            }}>
              {error}
            </div>
          )}

          {/* Start */}
          <Section label="Start">
            <TimeStepper label="Time" value={start} onChange={setStart} />
            <LocButton loc={startLoc} onClick={() => setPickerFor('start')} />
          </Section>

          {/* End */}
          <Section label="End">
            <TimeStepper label="Time" value={end} onChange={setEnd} />
            <LocButton loc={endLoc} onClick={() => setPickerFor('end')} />
          </Section>

          {/* Break — shown when a break step exists or route already has break
              times. Locked once the route has pickup/dropoff steps (§4.7). */}
          {hasBreak && (
            <Section label="Break">
              {breakLocked ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', textAlign: 'center' }}>
                    {breakStart && breakEnd ? `${breakStart} – ${breakEnd}` : '—'}
                  </div>
                  <div style={{
                    marginTop: 8,
                    background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10,
                    padding: '10px 12px', fontSize: 12, color: '#94A3B8', lineHeight: 1.4,
                  }}>
                    🔒&nbsp; Breaks can&apos;t be changed once trips are on this route.
                  </div>
                </>
              ) : (
                <>
                  <TimeStepper label="From"  value={breakStart} onChange={setBreakStart} />
                  <div style={{ height: 8 }} />
                  <TimeStepper label="Until" value={breakEnd}   onChange={setBreakEnd} />
                  {breakMins !== null && (
                    <div style={{ fontSize: 12, color: '#64748B', textAlign: 'center', marginTop: 6 }}>
                      {breakMins} minute break
                    </div>
                  )}
                  {(breakStart || breakEnd) && (
                    <button
                      type="button"
                      onClick={() => { setBreakStart(''); setBreakEnd(''); }}
                      style={{
                        display: 'block', marginTop: 8, background: 'none', border: 'none',
                        color: '#EF4444', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0,
                      }}
                    >
                      Remove break
                    </button>
                  )}
                </>
              )}
            </Section>
          )}

          {/* Accepting trips toggle — writes Route.IsInactive (comms.setRouteActive).
              Disabling stops dispatch from adding new trips; it does not remove the
              route or its trips already assigned, and the driver still drives it. */}
          <Section label="Accepting trips">
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: 12,
              padding: '12px 14px',
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>
                  {isRouteActive ? 'Route is active' : 'Route is disabled'}
                </div>
                <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                  {isRouteActive
                    ? 'Dispatch can assign new trips to this route.'
                    : 'No new trips will be added to this route.'}
                </div>
              </div>
              <button
                type="button"
                onClick={handleToggleActive}
                disabled={!online || togglingActive}
                style={{
                  padding: '9px 16px', borderRadius: 10, border: 'none',
                  fontSize: 13, fontWeight: 700,
                  background: isRouteActive ? '#FEF2F2' : '#EFF6FF',
                  color: isRouteActive ? '#DC2626' : '#3B5BDB',
                  cursor: !online || togglingActive ? 'not-allowed' : 'pointer',
                  opacity: !online || togglingActive ? 0.6 : 1,
                  flexShrink: 0,
                }}
              >
                {togglingActive ? 'Saving…' : isRouteActive ? 'Disable' : 'Enable'}
              </button>
            </div>
          </Section>

          <div style={{ height: 1, background: '#F1F5F9', margin: '0 0 20px' }} />

          {/* Split route */}
          <button
            type="button"
            disabled={!canSplit}
            onClick={() => setSplitOpen(true)}
            style={{
              width: '100%', padding: '13px', borderRadius: 14,
              border: canSplit ? '2px dashed #3B5BDB' : '2px dashed #E2E8F0',
              background: 'transparent',
              color: canSplit ? '#3B5BDB' : '#CBD5E1',
              fontSize: 14, fontWeight: 700,
              cursor: canSplit ? 'pointer' : 'not-allowed',
            }}
          >
            ✂&nbsp;&nbsp;Split into two routes
          </button>
          <div style={{ fontSize: 11, color: '#CBD5E1', textAlign: 'center', marginTop: 4 }}>
            {hasTrips
              ? 'Not available once trips are on this route'
              : serviceDate <= todayStr
              ? 'Only available for a future date'
              : 'Splits this day only — your weekly schedule is unchanged'}
          </div>
        </DialogContent>

        <DialogActions style={{ padding: '8px 20px 20px', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1, padding: '13px', borderRadius: 14, border: 'none',
              background: '#F1F5F9', color: '#64748B', fontSize: 14, fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !online}
            style={{
              flex: 1, padding: '13px', borderRadius: 14, border: 'none',
              background: saving || !online ? '#93C5FD' : '#3B5BDB',
              color: '#fff', fontSize: 14, fontWeight: 700,
              cursor: saving || !online ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : !online ? 'Offline' : 'Save changes'}
          </button>
        </DialogActions>
      </Dialog>

      <LocationPicker
        open={!!pickerFor}
        onClose={() => setPickerFor(null)}
        driverId={driverId}
        online={online}
        onToast={onToast}
        onSelect={(loc) => {
          const sel = { id: loc.Id, name: loc.Name };
          if (pickerFor === 'start') setStartLoc(sel);
          else setEndLoc(sel);
          setPickerFor(null);
        }}
      />

      <SplitRouteDialog
        open={splitOpen}
        onClose={() => setSplitOpen(false)}
        route={route}
        driverId={driverId}
        online={online}
        onToast={onToast}
        serviceDate={serviceDate}
        start={start}
        end={end}
        startLoc={startLoc}
        endLoc={endLoc}
        onSplit={() => {
          // The route this sheet was editing no longer exists in its old form —
          // segment 1 overwrote it and segment 2 is a brand-new Route. Close this
          // editor and let the caller do a full day reload rather than trying to
          // patch a single route in place (see RouteEditorSheet.js's onSaved path).
          if (onSplit) onSplit();
          onClose();
        }}
      />
    </>
  );
}

export default RouteEditorSheet;
